import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { buildRpmOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard/utils';
import { API_INTERACTIVE_TIME_DASHBOARD_OVERVIEW_SLA } from 'services/dataService/resources/interactiveTimeDashboard';
import useUser from 'utils/useUser';

import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard/use-filter';
import type { RpmOverviewPatientStatusCount } from 'infrastructure/hooks/rpm/overview-dashboard/use-rpm-overview-dashboard-count';
import type { IAssignee } from 'infrastructure/interfaces';
import type {
  ClinicDoNotCallRestrictionType,
  DoNotCallRestrictionType,
} from 'infrastructure/enums';

export interface ISlaPatientRow {
  guid: string;
  name: string | null;
  podSection: 'primary' | 'secondary' | 'tertiary' | 'outside' | null;
  podName: string | null;
  provider: string | null;
  dob: string | null;
  language: string | null;
  assignee: IAssignee | null;
  readings: RpmOverviewPatientStatusCount | null;
  overTime: boolean | null;
  pmpmStatus: boolean | null;
  interactionStatus: boolean | null;
  timezone: string | null;
  doNotCallRestrictionType: DoNotCallRestrictionType | null;
  doNotCallStartTime: string | null;
  doNotCallEndTime: string | null;
  clinicDoNotCallRestrictionType: ClinicDoNotCallRestrictionType | null;
  clinicDoNotCallStartTime: string | null;
  clinicDoNotCallEndTime: string | null;
  clinicGuid: string | null;
  // SLA-specific fields
  slaDeadline: string | null;
  slaBreachStatus: string | null;
}

export interface ISlaListResponse {
  patients: ISlaPatientRow[];
  hasMore: boolean; // BE uses over-fetch hasMore pattern — no totalCount
}

// Maximum pageSize accepted by the BE slaSchema (pageSize.max(1000)).
// Used both in the API call (fetches all items at once — SLA has no UI pagination)
// and in RpmOverviewTable to prevent AntD internal pagination controls from appearing.
export const SLA_PAGE_SIZE = 1000;

export const getSlaListQueryKey = (filter: TOverviewDashboardFilter) =>
  ['rpm-overview-sla', filter] as const;

const useSlaList = (filter: TOverviewDashboardFilter) => {
  const { agencies, hasRpmPodAssignment } = useUser();
  const agencyGuid = agencies.map((a) => a.guid);

  const transformedFilter = buildRpmOverviewDashboardFilter(filter);

  return useQuery<ISlaListResponse>({
    queryKey: getSlaListQueryKey(filter),
    keepPreviousData: true,
    queryFn: async () => {
      const { data, error } = await dataService.getList<ISlaListResponse>(
        apiUrlGenerator(API_INTERACTIVE_TIME_DASHBOARD_OVERVIEW_SLA, {
          ...transformedFilter,
          agencyGuid,
          pageSize: SLA_PAGE_SIZE,
        }),
      );

      if (error) {
        return Promise.reject(error);
      }

      return {
        patients: (data?.patients ?? []) as ISlaPatientRow[],
        hasMore: (data?.hasMore ?? false) as boolean,
      };
    },
    enabled: agencyGuid.length > 0 && hasRpmPodAssignment,
  });
};

export default useSlaList;
